import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { Mutation, MutationBlockNfcCardArgs, Query } from 'graphql/generated/graphqlTypes';
import { BLOCK_NFC_CARD } from 'graphql/mutations';
import { GET_CARDS } from 'graphql/queries';
import { useEffect } from 'react';
import { setCardsAction, updateCardsAction } from 'state/actions/cards';
import { cardsVar } from 'state/vars';
import { BaseHookProps } from '../shared/types';

const useCards = ({ handleFetchError }: BaseHookProps) => {
  const cards = useReactiveVar(cardsVar);

  const [getCards, { loading }] = useLazyQuery<Pick<Query, 'getNfcCards'>>(GET_CARDS, {
    onCompleted: (data) => {
      if (data.getNfcCards) {
        setCardsAction(data.getNfcCards);
      }
    },
    onError: () => {
      handleFetchError('Error while fetching cards');
    },
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    getCards();
  }, []);

  const [updateBlockNfcCardMutation, { loading: updateBlockNfcCardMutationLoading }] = useMutation<
    Pick<Mutation, 'blockNfcCard'>,
    MutationBlockNfcCardArgs
  >(BLOCK_NFC_CARD, {
    onCompleted: (data) => {
      updateCardsAction(data.blockNfcCard);
    },
    onError: () => {
      handleFetchError('Error while blocking card');
    }
  });

  const updateBlockNfcCard = async (reason: string, uuid: string) => {
    await updateBlockNfcCardMutation({
      variables: {
        reason,
        uuid
      },
      // Here Apollo doesn't update cache automatically, we have to do it manually
      update(cache, { data: updatedCard }) {
        cache.writeQuery({
          query: GET_CARDS,
          data: {
            getDoorsForTenant: cards?.map((card) =>
              card.uuid === updatedCard?.blockNfcCard?.uuid ? updatedCard : card
            )
          }
        });
      }
    });
  };

  return {
    cards,
    loading,
    updateBlockNfcCard,
    updateBlockNfcCardMutationLoading
  };
};

export default useCards;
